import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiFetch } from "@/lib/api-client";
import type { ApiPiece } from "@/hooks/use-projects";
import { toast } from "sonner";

// --- Raw API functions (exported for testing) ---

export interface PieceFilters {
  status?: string;
  projectId?: string;
  platform?: string;
  postType?: string;
  page?: number;
  pageSize?: number;
}

export interface PieceWithProject extends ApiPiece {
  project: { id: string; title: string };
}

export const piecesApi = {
  fetchBrandPieces: async (brandId: string, filters: PieceFilters = {}) => {
    const params = new URLSearchParams();
    if (filters.status) params.set("status", filters.status);
    if (filters.projectId) params.set("projectId", filters.projectId);
    if (filters.platform) params.set("platform", filters.platform);
    if (filters.postType) params.set("postType", filters.postType);
    if (filters.page) params.set("page", String(filters.page));
    if (filters.pageSize) params.set("pageSize", String(filters.pageSize));

    const qs = params.toString();
    const url = `/brands/${brandId}/pieces${qs ? `?${qs}` : ""}`;

    // This endpoint returns { data, pagination } — we need the full response
    // so we use a raw fetch instead of apiFetch (which extracts .data)
    const res = await fetch(`/api${url}`, {
      credentials: "include",
      headers: { "Content-Type": "application/json" },
    });
    if (!res.ok) throw new Error("Failed to fetch pieces");
    return res.json() as Promise<{
      data: PieceWithProject[];
      pagination: { total: number; page: number; pageSize: number; totalPages: number };
    }>;
  },

  fetchProjectPieces: (brandId: string, projectId: string) =>
    apiFetch<ApiPiece[]>(`/brands/${brandId}/projects/${projectId}/pieces`),

  fetchPiece: (brandId: string, pieceId: string) =>
    apiFetch<ApiPiece>(`/brands/${brandId}/pieces/${pieceId}`),

  fetchPieceByCode: (brandId: string, shortCode: string) =>
    apiFetch<ApiPiece>(`/brands/${brandId}/pieces/by-code/${shortCode}`),

  submitPiece: (brandId: string, pieceId: string) =>
    apiFetch<ApiPiece>(`/brands/${brandId}/pieces/${pieceId}/submit`, { method: "POST" }),

  approvePiece: (brandId: string, pieceId: string) =>
    apiFetch<ApiPiece>(`/brands/${brandId}/pieces/${pieceId}/approve`, { method: "POST" }),

  rejectPiece: (brandId: string, pieceId: string, reason: string) =>
    apiFetch<ApiPiece>(`/brands/${brandId}/pieces/${pieceId}/reject`, {
      method: "POST",
      body: JSON.stringify({ reason }),
    }),

  revisePiece: (brandId: string, pieceId: string) =>
    apiFetch<ApiPiece>(`/brands/${brandId}/pieces/${pieceId}/revise`, { method: "POST" }),

  updatePiece: (
    brandId: string,
    pieceId: string,
    fields: Partial<
      Pick<
        ApiPiece,
        "copy" | "caption" | "hashtags" | "title" | "description" | "threadItems" | "platformKnobs" | "mediaUrls"
      >
    >,
  ) =>
    apiFetch<ApiPiece>(`/brands/${brandId}/pieces/${pieceId}`, {
      method: "PATCH",
      body: JSON.stringify(fields),
    }),

  regenerateMedia: (brandId: string, pieceId: string) =>
    apiFetch<{ derivativeCount: number; warnings: string[] }>(
      `/brands/${brandId}/pieces/${pieceId}/regenerate-media`,
      { method: "POST" },
    ),
};

// --- TanStack Query hooks ---

export function useBrandPieces(brandId: string, filters: PieceFilters = {}) {
  return useQuery({
    queryKey: ["brandPieces", brandId, filters],
    queryFn: () => piecesApi.fetchBrandPieces(brandId, filters),
    enabled: !!brandId,
  });
}

export function usePieceByCode(brandId: string, shortCode: string) {
  return useQuery({
    queryKey: ["pieceByCode", brandId, shortCode],
    queryFn: () => piecesApi.fetchPieceByCode(brandId, shortCode),
    enabled: !!brandId && !!shortCode,
  });
}

export function useProjectPieces(brandId: string, projectId: string) {
  return useQuery({
    queryKey: ["projectPieces", brandId, projectId],
    queryFn: () => piecesApi.fetchProjectPieces(brandId, projectId),
    enabled: !!brandId && !!projectId,
  });
}

export function useApprovePiece(brandId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (pieceId: string) => piecesApi.approvePiece(brandId, pieceId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["brandPieces", brandId] });
      qc.invalidateQueries({ queryKey: ["projectPieces", brandId] });
      qc.invalidateQueries({ queryKey: ["pieceByCode", brandId] });
      toast.success("Piece approved");
    },
    onError: () => toast.error("Failed to approve piece"),
  });
}

export function useRejectPiece(brandId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ pieceId, reason }: { pieceId: string; reason: string }) =>
      piecesApi.rejectPiece(brandId, pieceId, reason),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["brandPieces", brandId] });
      qc.invalidateQueries({ queryKey: ["projectPieces", brandId] });
      qc.invalidateQueries({ queryKey: ["pieceByCode", brandId] });
      toast.success("Piece rejected");
    },
    onError: () => toast.error("Failed to reject piece"),
  });
}

export function useSubmitPiece(brandId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (pieceId: string) => piecesApi.submitPiece(brandId, pieceId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["brandPieces", brandId] });
      qc.invalidateQueries({ queryKey: ["projectPieces", brandId] });
      qc.invalidateQueries({ queryKey: ["pieceByCode", brandId] });
      toast.success("Piece submitted for review");
    },
    onError: () => toast.error("Failed to submit piece"),
  });
}

export function useRevisePiece(brandId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (pieceId: string) => piecesApi.revisePiece(brandId, pieceId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["brandPieces", brandId] });
      qc.invalidateQueries({ queryKey: ["projectPieces", brandId] });
      qc.invalidateQueries({ queryKey: ["pieceByCode", brandId] });
      toast.success("Piece moved to draft for revision");
    },
    onError: () => toast.error("Failed to revise piece"),
  });
}

export function useUpdatePiece(brandId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      pieceId,
      fields,
    }: {
      pieceId: string;
      fields: Partial<
        Pick<
          ApiPiece,
          "copy" | "caption" | "hashtags" | "title" | "description" | "threadItems" | "platformKnobs" | "mediaUrls"
        >
      >;
    }) => piecesApi.updatePiece(brandId, pieceId, fields),
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: ["brandPieces", brandId] });
      qc.invalidateQueries({ queryKey: ["projectPieces", brandId] });
      qc.invalidateQueries({ queryKey: ["pieceByCode", brandId] });
      if ((data as ApiPiece & { requiresReapproval?: boolean }).requiresReapproval) {
        toast.info("Piece updated. Requires re-approval.");
      } else {
        toast.success("Piece updated");
      }
    },
    onError: () => toast.error("Failed to update piece"),
  });
}

export function useRegenerateMedia(brandId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (pieceId: string) => piecesApi.regenerateMedia(brandId, pieceId),
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: ["brandPieces", brandId] });
      qc.invalidateQueries({ queryKey: ["projectPieces", brandId] });
      qc.invalidateQueries({ queryKey: ["pieceByCode", brandId] });
      const count = data.derivativeCount;
      const warnings = data.warnings ?? [];
      if (warnings.length > 0) {
        toast.warning(`Re-optimized ${count} (${warnings.length} skipped)`);
      } else {
        toast.success(`Re-optimized ${count} media item${count === 1 ? "" : "s"}`);
      }
    },
    onError: () => toast.error("Failed to re-optimize media"),
  });
}
